import React, { useLayoutEffect, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { CVData, SectionKey } from '../../types/cv';
import { findSectionForItemId } from '../../utils/cvPagination';
import { OrganizerDragPayload } from './PageOrganizerPanel';

type HandlePos = {
    itemId: string;
    section: SectionKey;
    top: number;
    left: number;
};

const BlockHandle: React.FC<{
    itemId: string;
    section: SectionKey;
    top: number;
    left: number;
}> = ({ itemId, section, top, left }) => {
    const payload: OrganizerDragPayload = { kind: 'item', itemId, section };
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `preview-item-${itemId}`,
        data: payload,
    });

    return (
        <button
            type="button"
            ref={setNodeRef}
            className={`absolute z-20 no-print print:hidden flex items-center justify-center w-7 h-7 rounded-full border shadow-md cursor-grab active:cursor-grabbing ${
                isDragging
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-white/95 border-indigo-200 text-indigo-600 hover:bg-indigo-50'
            }`}
            style={{
                top,
                left,
                transform: CSS.Translate.toString(transform),
            }}
            title="Glisser vers une autre page"
            {...listeners}
            {...attributes}
        >
            <GripVertical className="w-3.5 h-3.5" />
        </button>
    );
};

/**
 * Poignées de drag sur les blocs [data-cv-item-id] de l’aperçu (mode Organisation).
 */
const ContentBlockDragHandles: React.FC<{
    rootRef: React.RefObject<HTMLElement | null>;
    enabled: boolean;
    data: CVData;
}> = ({ rootRef, enabled, data }) => {
    const [handles, setHandles] = useState<HandlePos[]>([]);

    useLayoutEffect(() => {
        if (!enabled) {
            setHandles([]);
            return;
        }

        const root = rootRef.current;
        if (!root) return;

        const update = () => {
            const rootRect = root.getBoundingClientRect();
            const next: HandlePos[] = [];
            root.querySelectorAll<HTMLElement>('[data-cv-item-id]').forEach((el) => {
                const itemId = el.getAttribute('data-cv-item-id');
                if (!itemId) return;
                const section = findSectionForItemId(data, itemId);
                if (!section) return;
                const r = el.getBoundingClientRect();
                if (r.height < 4 || r.width < 4) return;
                next.push({
                    itemId,
                    section,
                    top: r.top - rootRect.top + 4,
                    left: Math.max(0, r.left - rootRect.left - 4),
                });
            });
            setHandles(next);
        };

        update();
        const ro = new ResizeObserver(() => update());
        ro.observe(root);
        const t = window.setTimeout(update, 80);
        return () => {
            ro.disconnect();
            clearTimeout(t);
        };
    }, [enabled, data, rootRef]);

    if (!enabled || handles.length === 0) return null;

    return (
        <div className="absolute inset-0 pointer-events-none z-20 no-print print:hidden">
            <div className="relative w-full h-full">
                {handles.map((h) => (
                    <div key={h.itemId} className="pointer-events-auto">
                        <BlockHandle {...h} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ContentBlockDragHandles;
